<template>
  <section-card
    :title="t('home_cockpit.notifications_card.title')"
    :subtitle="subtitle"
    :is-loading="!demo && isLoading && items.length === 0"
    :is-empty="!demo && !isLoading && items.length === 0"
    :empty-title="t('home_cockpit.notifications_card.empty_title')"
    :empty-message="t('home_cockpit.notifications_card.empty_message')"
  >
    <!-- View-all link in the section header action slot, matching the existing
         "Vraagt aandacht" placement so the homepage rhythm stays the same. -->
    <template #action>
      <router-link
        to="/notifications"
        class="inline-flex items-center gap-1 text-xs font-medium text-theme-palette-emerald hover:text-theme-palette-emerald dark:text-theme-palette-emerald dark:hover:text-theme-palette-emerald"
      >
        <span>{{ t('home_cockpit.notifications_card.view_all') }}</span>
        <icon :path="mdiArrowRight" size="14" />
      </router-link>
    </template>

    <template #loadingSkeleton>
      <div class="space-y-2" aria-hidden="true">
        <div
          v-for="i in 3"
          :key="`ntf-skel-${i}`"
          class="flex items-center gap-3 py-1"
        >
          <skeleton-text width="10px" height="10px" variant="circle" />
          <div class="flex-1 min-w-0 space-y-1">
            <skeleton-text :width="i === 1 ? '70%' : i === 2 ? '55%' : '60%'" height="14px" />
            <skeleton-text width="35%" height="10px" />
          </div>
          <skeleton-text width="40px" height="12px" />
        </div>
      </div>
    </template>

    <div v-if="demo" class="space-y-3 py-3" data-test="demo-inbox-locked">
      <icon :path="mdiLockOutline" class="text-theme-brand" :size="24" />
      <p class="text-sm text-theme-text-secondary">{{ t('portal_demo.inbox_body') }}</p>
      <app-button to="/subscriptions?intent=platform" :label="t('portal_demo.plans')" variant="outline" size="sm" />
    </div>
    <div v-else class="divide-y divide-theme-border dark:divide-theme-border">
      <notification-item
        v-for="n in items"
        :key="n.id"
        :notification="n"
        @mark-read="onMarkRead"
        @archive="onArchive"
        @delete="onDelete"
      />
    </div>
  </section-card>
</template>

<script setup>
  /* eslint-disable no-undef */
  import NotificationItem from '@/features/notifications/components/NotificationItem.vue';
  import Icon from '@/components/Icon';
  import SectionCard from '@/components/ui/data-display/SectionCard.vue';
  import SkeletonText from '@/components/ui/states/SkeletonText.vue';
  import { useNotificationsStore } from '@/features/notifications/stores/useNotificationsStore';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import { mdiArrowRight, mdiLockOutline } from '@mdi/js';
  import { storeToRefs } from 'pinia';
  import { computed, onMounted } from 'vue';
  import { useI18n } from 'vue-i18n';

  // How many notifications to surface in the homepage card. The store
  // fetches 25 per page by default — we just slice the freshest 6 here.
  const HOME_CARD_LIMIT = 6;

  const props = defineProps({ demo: Boolean });
  const { t } = useI18n();
  const notificationsStore = useNotificationsStore();
  const { unreadCount, pagination } = storeToRefs(notificationsStore);

  const items = computed(() => notificationsStore.items.slice(0, HOME_CARD_LIMIT));

  const isLoading = computed(() => Boolean(pagination.value?.loading));

  const subtitle = computed(() => {
    if (props.demo) return t('portal_demo.locked_title');
    if (unreadCount.value <= 0) {
      return t('home_cockpit.notifications_card.subtitle_zero');
    }
    return t('home_cockpit.notifications_card.subtitle_unread', unreadCount.value, {
      count: unreadCount.value,
    });
  });

  onMounted(async () => {
    // Reuse the notifications store's fetch — it powers the bell, dropdown,
    // and center page too. Calling it here lazy-loads the first page when the
    // user lands on the home cockpit.
    if (!props.demo) await notificationsStore.fetchPage({ reset: true });
  });

  async function onMarkRead(id) {
    try {
      await notificationsStore.markRead(id);
    } catch {
      // ignore — store action handles rollback
    }
  }
  async function onArchive(_id) {
    // No archive endpoint yet — kept as a no-op so the template event still
    // wires cleanly. Implement when the backend exposes the action.
  }
  async function onDelete(id) {
    try {
      await notificationsStore.deleteNotification(id);
    } catch {
      // ignore
    }
  }

  defineExpose({
    refresh: () => props.demo ? undefined : notificationsStore.fetchPage({ reset: true }),
  });
</script>
